// TMOS Position Templates Page (Platform Admin)
// Position templates = named bundles of positions. Provision entire template to tenant.
// Position library = platform-level atomic positions used inside templates.

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { positionTemplateService, PositionTemplate } from '@/services/positionTemplateService';
import { businessPositionService, BusinessPosition } from '@/services/businessPositionService';
import api from '@/services/api';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowRightCircleIcon,
  BriefcaseIcon,
  CubeIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  SquaresPlusIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

const CATEGORIES = ['EXECUTIVE', 'LEADERSHIP', 'MANAGEMENT', 'OPERATIONAL', 'PROFESSIONAL_SERVICES'] as const;

export const PositionTemplatesPage: React.FC = () => {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState<PositionTemplate[]>([]);
  const [library, setLibrary] = useState<BusinessPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [provisionModalOpen, setProvisionModalOpen] = useState(false);
  const [editTemplateModalOpen, setEditTemplateModalOpen] = useState(false);
  const [libraryModalOpen, setLibraryModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<PositionTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({ template_code: '', template_name: '', description: '' });
  const [positionForm, setPositionForm] = useState({
    position_code: '',
    position_name: '',
    position_category: '' as string,
    description: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [provisioning, setProvisioning] = useState(false);
  const [editingPositionId, setEditingPositionId] = useState<string | null>(null);
  const [addPositionToTemplateId, setAddPositionToTemplateId] = useState<string | null>(null);
  const [platformPositions, setPlatformPositions] = useState<BusinessPosition[]>([]);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceTemplate, setCopySourceTemplate] = useState<PositionTemplate | null>(null);
  const [copyForm, setCopyForm] = useState({ template_code: '', template_name: '', description: '' });
  const [addingAll, setAddingAll] = useState(false);
  const [copying, setCopying] = useState(false);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const list = await positionTemplateService.list();
      setTemplates(list);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number }; message?: string };
      if (e?.response?.status === 403) {
        toast.error(t('positionTemplates.platformAdminRequired') || 'Platform administrator access required');
      } else {
        toast.error(e?.message || 'Failed to load templates');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLibrary = async () => {
    try {
      setLibraryLoading(true);
      const list = await businessPositionService.getPlatformTemplates();
      setLibrary(list);
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e?.message || 'Failed to load position library');
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    loadLibrary();
  }, []);

  const openCreateTemplate = () => {
    setEditingTemplateId(null);
    setTemplateForm({ template_code: '', template_name: '', description: '' });
    setModalOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.template_code?.trim() || !templateForm.template_name?.trim()) {
      toast.error('Template code and name are required');
      return;
    }
    try {
      setSaving(true);
      if (editingTemplateId) {
        await positionTemplateService.update(editingTemplateId, {
          template_name: templateForm.template_name.trim(),
          description: templateForm.description?.trim() || null
        });
        toast.success('Template updated');
      } else {
        await positionTemplateService.create({
          template_code: templateForm.template_code.trim().toUpperCase(),
          template_name: templateForm.template_name.trim(),
          description: templateForm.description?.trim() || null
        });
        toast.success('Template created');
      }
      setModalOpen(false);
      loadTemplates();
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openEditTemplate = async (template: PositionTemplate) => {
    setEditingTemplateId(template.id);
    const full = await positionTemplateService.getById(template.id);
    setEditingTemplate(full);
    setTemplateForm({
      template_code: full.template_code,
      template_name: full.template_name,
      description: full.description ?? ''
    });
    setPlatformPositions(await businessPositionService.getPlatformTemplates());
    setEditTemplateModalOpen(true);
  };

  const handleUpdateTemplateMeta = async () => {
    if (!editingTemplateId) return;
    if (!templateForm.template_name?.trim()) {
      toast.error('Template name is required');
      return;
    }
    try {
      setSaving(true);
      await positionTemplateService.update(editingTemplateId, {
        template_name: templateForm.template_name.trim(),
        description: templateForm.description?.trim() || null
      });
      const full = await positionTemplateService.getById(editingTemplateId);
      setEditingTemplate(full);
      toast.success('Template updated');
      loadTemplates();
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const removePositionFromTemplate = async (positionId: string) => {
    if (!editingTemplateId) return;
    try {
      await positionTemplateService.removePosition(editingTemplateId, positionId);
      const full = await positionTemplateService.getById(editingTemplateId);
      setEditingTemplate(full);
      loadTemplates();
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to remove');
    }
  };

  const addPositionToTemplate = async (positionId: string) => {
    if (!editingTemplateId) return;
    try {
      await positionTemplateService.addPosition(editingTemplateId, positionId);
      const full = await positionTemplateService.getById(editingTemplateId);
      setEditingTemplate(full);
      setAddPositionToTemplateId(null);
      loadTemplates();
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to add');
    }
  };

  const addAllPositionsToTemplate = async () => {
    if (!editingTemplateId || availableToAdd.length === 0) return;
    try {
      setAddingAll(true);
      await positionTemplateService.addPositionsBulk(editingTemplateId, availableToAdd.map(p => p.id));
      const full = await positionTemplateService.getById(editingTemplateId);
      setEditingTemplate(full);
      setAddPositionToTemplateId(null);
      loadTemplates();
      toast.success(`Added ${availableToAdd.length} position(s) to template`);
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to add positions');
    } finally {
      setAddingAll(false);
    }
  };

  const openCopyModal = (tpl: PositionTemplate) => {
    setCopySourceTemplate(tpl);
    setCopyForm({
      template_code: `${tpl.template_code}_COPY`,
      template_name: `${tpl.template_name} (copy)`,
      description: tpl.description ?? ''
    });
    setCopyModalOpen(true);
  };

  const handleCopyTemplate = async () => {
    if (!copySourceTemplate) return;
    if (!copyForm.template_code?.trim() || !copyForm.template_name?.trim()) {
      toast.error('Template code and name are required');
      return;
    }
    try {
      setCopying(true);
      const created = await positionTemplateService.copyTemplate(copySourceTemplate.id, {
        template_code: copyForm.template_code.trim().toUpperCase(),
        template_name: copyForm.template_name.trim(),
        description: copyForm.description?.trim() || null
      });
      toast.success('Template copied');
      setCopyModalOpen(false);
      setCopySourceTemplate(null);
      loadTemplates();
      openEditTemplate(created);
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to copy template');
    } finally {
      setCopying(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Delete this template? Positions in the library are not deleted.')) return;
    try {
      await positionTemplateService.delete(id);
      toast.success('Template deleted');
      setEditTemplateModalOpen(false);
      loadTemplates();
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to delete');
    }
  };

  const openProvision = () => {
    setSelectedTenantId('');
    setSelectedTemplateId('');
    setProvisionModalOpen(true);
    (async () => {
      try {
        const res = await api.get('/tenants');
        const data = res.data?.success ? res.data?.data : res.data;
        const list = Array.isArray(data) ? data : [];
        setTenants(list.map((x: { id: string; tenant_name?: string; name?: string }) => ({ id: x.id, name: x.tenant_name || x.name || x.id })));
      } catch {
        setTenants([]);
      }
    })();
  };

  const handleProvision = async () => {
    if (!selectedTenantId || !selectedTemplateId) {
      toast.error('Select a tenant and a template');
      return;
    }
    try {
      setProvisioning(true);
      await positionTemplateService.provisionToTenant(selectedTenantId, selectedTemplateId);
      toast.success('Template provisioned to tenant');
      setProvisionModalOpen(false);
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to provision');
    } finally {
      setProvisioning(false);
    }
  };

  const openCreatePosition = () => {
    setEditingPositionId(null);
    setPositionForm({
      position_code: '',
      position_name: '',
      position_category: '',
      description: '',
      is_active: true
    });
    setLibraryModalOpen(true);
  };

  const openEditPosition = (p: BusinessPosition) => {
    setEditingPositionId(p.id);
    setPositionForm({
      position_code: p.position_code,
      position_name: p.position_name,
      position_category: p.position_category || '',
      description: p.description || '',
      is_active: p.is_active !== false
    });
    setLibraryModalOpen(true);
  };

  const handleSavePosition = async () => {
    if (!positionForm.position_code?.trim() || !positionForm.position_name?.trim()) {
      toast.error('Position code and name are required');
      return;
    }
    try {
      setSaving(true);
      if (editingPositionId) {
        await businessPositionService.updatePlatformTemplate(editingPositionId, {
          position_name: positionForm.position_name.trim(),
          position_category: positionForm.position_category || undefined,
          description: positionForm.description?.trim() || undefined,
          is_active: positionForm.is_active
        });
        toast.success('Position updated');
      } else {
        await businessPositionService.createPlatformTemplate({
          position_code: positionForm.position_code.trim().toUpperCase(),
          position_name: positionForm.position_name.trim(),
          position_category: positionForm.position_category || undefined,
          description: positionForm.description?.trim() || undefined,
          is_active: positionForm.is_active
        });
        toast.success('Position created');
      }
      setLibraryModalOpen(false);
      loadLibrary();
      if (editTemplateModalOpen && editingTemplateId) {
        setPlatformPositions(await businessPositionService.getPlatformTemplates());
      }
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePosition = async (id: string) => {
    if (!confirm('Delete this position from the library?')) return;
    try {
      await businessPositionService.deletePlatformTemplate(id);
      toast.success('Position deleted');
      loadLibrary();
      if (editTemplateModalOpen) loadTemplates();
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to delete');
    }
  };

  const positionsInTemplate = editingTemplate?.positions ?? [];
  const positionIdsInTemplate = new Set(positionsInTemplate.map(p => p.id));
  const availableToAdd = platformPositions.filter(p => !positionIdsInTemplate.has(p.id));

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--color-surface-text)] mb-2">
            {t('positionTemplates.title') || 'Position Templates'}
          </h1>
          <p className="text-[var(--color-secondary-text)]">
            Templates are bundles of positions. Provision a template to a tenant to copy all its positions. Manage the position library below.
          </p>
        </div>

        {/* Position templates (bundles) */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CubeIcon className="w-5 h-5" />
              Templates
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={openProvision} leftIcon={<ArrowRightCircleIcon className="w-5 h-5" />}>
                Provision to tenant
              </Button>
              <Button onClick={openCreateTemplate} leftIcon={<PlusIcon className="w-5 h-5" />}>
                Add template
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-[var(--color-secondary-text)]">Loading...</p>
            ) : templates.length === 0 ? (
              <p className="text-[var(--color-secondary-text)]">No templates yet. Create a template and add positions from the library.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="py-2 pr-4 font-medium text-[var(--color-surface-text)]">Code</th>
                      <th className="py-2 pr-4 font-medium text-[var(--color-surface-text)]">Name</th>
                      <th className="py-2 pr-4 font-medium text-[var(--color-surface-text)]">Positions</th>
                      <th className="py-2 font-medium text-[var(--color-surface-text)]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map((tpl) => (
                      <tr key={tpl.id} className="border-b border-[var(--color-border)]">
                        <td className="py-2 pr-4 font-mono text-sm">{tpl.template_code}</td>
                        <td className="py-2 pr-4">{tpl.template_name}</td>
                        <td className="py-2 pr-4">
                          <Badge variant="default">{(tpl.position_count ?? 0)}</Badge>
                        </td>
                        <td className="py-2 flex gap-2 flex-wrap">
                          <Button variant="ghost" size="sm" onClick={() => openEditTemplate(tpl)} leftIcon={<PencilIcon className="w-4 h-4" />}>
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openCopyModal(tpl)} leftIcon={<DocumentDuplicateIcon className="w-4 h-4" />}>
                            Copy
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(tpl.id)} leftIcon={<TrashIcon className="w-4 h-4" />}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Position library (platform positions) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BriefcaseIcon className="w-5 h-5" />
              Position library
            </CardTitle>
            <Button onClick={openCreatePosition} leftIcon={<PlusIcon className="w-5 h-5" />}>
              Add position
            </Button>
          </CardHeader>
          <CardContent>
            {libraryLoading ? (
              <p className="text-[var(--color-secondary-text)]">Loading...</p>
            ) : library.length === 0 ? (
              <p className="text-[var(--color-secondary-text)]">No positions in library. Add positions here; then add them to templates above.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="py-2 pr-4 font-medium text-[var(--color-surface-text)]">Code</th>
                      <th className="py-2 pr-4 font-medium text-[var(--color-surface-text)]">Name</th>
                      <th className="py-2 pr-4 font-medium text-[var(--color-surface-text)]">Category</th>
                      <th className="py-2 font-medium text-[var(--color-surface-text)]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {library.map((p) => (
                      <tr key={p.id} className="border-b border-[var(--color-border)]">
                        <td className="py-2 pr-4 font-mono text-sm">{p.position_code}</td>
                        <td className="py-2 pr-4">{p.position_name}</td>
                        <td className="py-2 pr-4">{p.position_category || '—'}</td>
                        <td className="py-2 flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditPosition(p)} leftIcon={<PencilIcon className="w-4 h-4" />}>
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeletePosition(p.id)} leftIcon={<TrashIcon className="w-4 h-4" />}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create template modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75" onClick={() => !saving && setModalOpen(false)}>
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-2xl ring-1 ring-black/20 max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">{editingTemplateId ? 'Edit template' : 'New template'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={templateForm.template_code}
                  onChange={e => setTemplateForm(f => ({ ...f, template_code: e.target.value }))}
                  placeholder="e.g. EXEC_TEAM"
                  disabled={!!editingTemplateId}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={templateForm.template_name}
                  onChange={e => setTemplateForm(f => ({ ...f, template_name: e.target.value }))}
                  placeholder="e.g. Executive team"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={templateForm.description}
                  onChange={e => setTemplateForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleSaveTemplate} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit template modal (name + positions in template) */}
      {editTemplateModalOpen && editingTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75" onClick={() => setEditTemplateModalOpen(false)}>
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-2xl ring-1 ring-black/20 max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Edit template: {editingTemplate.template_name}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={templateForm.template_name}
                  onChange={e => setTemplateForm(f => ({ ...f, template_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={templateForm.description}
                  onChange={e => setTemplateForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                  <label className="block text-sm font-medium">Positions in this template</label>
                  <div className="flex gap-2">
                    {availableToAdd.length > 0 && (
                      <Button variant="secondary" size="sm" onClick={addAllPositionsToTemplate} disabled={addingAll} leftIcon={<SquaresPlusIcon className="w-4 h-4" />}>
                        {addingAll ? 'Adding...' : `Add all (${availableToAdd.length})`}
                      </Button>
                    )}
                    {addPositionToTemplateId !== editingTemplateId ? (
                      <Button variant="ghost" size="sm" onClick={() => setAddPositionToTemplateId(editingTemplateId)}>Add position</Button>
                    ) : null}
                  </div>
                </div>
                {addPositionToTemplateId === editingTemplateId && availableToAdd.length > 0 && (
                  <div className="mb-2 p-2 border border-[var(--color-border)] rounded flex flex-wrap gap-2">
                    {availableToAdd.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        className="px-2 py-1 rounded bg-[var(--color-secondary-bg)] hover:bg-[var(--color-border)] text-sm"
                        onClick={() => addPositionToTemplate(p.id)}
                      >
                        + {p.position_name}
                      </button>
                    ))}
                    <button type="button" className="text-[var(--color-secondary-text)] text-sm" onClick={() => setAddPositionToTemplateId(null)}>Cancel</button>
                  </div>
                )}
                <ul className="border border-[var(--color-border)] rounded divide-y divide-[var(--color-border)]">
                  {positionsInTemplate.length === 0 ? (
                    <li className="px-3 py-2 text-[var(--color-secondary-text)]">No positions. Add from library above.</li>
                  ) : (
                    positionsInTemplate.map(p => (
                      <li key={p.id} className="px-3 py-2 flex justify-between items-center">
                        <span>{p.position_name} <span className="text-[var(--color-secondary-text)] text-sm">({p.position_code})</span></span>
                        <button type="button" className="text-red-600 hover:underline" onClick={() => removePositionFromTemplate(p.id)} aria-label="Remove">
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditTemplateModalOpen(false)} disabled={saving}>Close</Button>
              <Button onClick={handleUpdateTemplateMeta} disabled={saving}>{saving ? 'Saving...' : 'Save name & description'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Copy template modal */}
      {copyModalOpen && copySourceTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75" onClick={() => !copying && setCopyModalOpen(false)}>
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-2xl ring-1 ring-black/20 max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Copy template: {copySourceTemplate.template_name}</h2>
            <p className="text-sm text-[var(--color-secondary-text)] mb-4">Creates a new template with the same positions. Enter a new code and name.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">New template code</label>
                <input
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={copyForm.template_code}
                  onChange={e => setCopyForm(f => ({ ...f, template_code: e.target.value }))}
                  placeholder="e.g. EXEC_TEAM_COPY"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">New template name</label>
                <input
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={copyForm.template_name}
                  onChange={e => setCopyForm(f => ({ ...f, template_name: e.target.value }))}
                  placeholder="e.g. Executive team (copy)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description (optional)</label>
                <textarea
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={copyForm.description}
                  onChange={e => setCopyForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setCopyModalOpen(false)} disabled={copying}>Cancel</Button>
              <Button onClick={handleCopyTemplate} disabled={copying || !copyForm.template_code?.trim() || !copyForm.template_name?.trim()}>
                {copying ? 'Copying...' : 'Copy template'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Provision to tenant modal */}
      {provisionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75" onClick={() => !provisioning && setProvisionModalOpen(false)}>
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-2xl ring-1 ring-black/20 max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Provision to tenant</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tenant</label>
                <select
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={selectedTenantId}
                  onChange={e => setSelectedTenantId(e.target.value)}
                >
                  <option value="">— Select tenant —</option>
                  {tenants.map(tn => (
                    <option key={tn.id} value={tn.id}>{tn.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Template (all its positions will be copied)</label>
                <select
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={selectedTemplateId}
                  onChange={e => setSelectedTemplateId(e.target.value)}
                >
                  <option value="">— Select template —</option>
                  {templates.map(tpl => (
                    <option key={tpl.id} value={tpl.id}>{tpl.template_name} ({(tpl.position_count ?? 0)} positions)</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setProvisionModalOpen(false)} disabled={provisioning}>Cancel</Button>
              <Button onClick={handleProvision} disabled={provisioning || !selectedTenantId || !selectedTemplateId}>
                {provisioning ? 'Provisioning...' : 'Provision'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Position library create/edit modal */}
      {libraryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75" onClick={() => !saving && setLibraryModalOpen(false)}>
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-2xl ring-1 ring-black/20 max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">{editingPositionId ? 'Edit position' : 'Add position to library'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={positionForm.position_code}
                  onChange={e => setPositionForm(f => ({ ...f, position_code: e.target.value }))}
                  placeholder="e.g. VP_ENG"
                  disabled={!!editingPositionId}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={positionForm.position_name}
                  onChange={e => setPositionForm(f => ({ ...f, position_name: e.target.value }))}
                  placeholder="e.g. VP Engineering"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={positionForm.position_category}
                  onChange={e => setPositionForm(f => ({ ...f, position_category: e.target.value }))}
                >
                  <option value="">—</option>
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)]"
                  value={positionForm.description}
                  onChange={e => setPositionForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                />
              </div>
              {editingPositionId && (
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={positionForm.is_active} onChange={e => setPositionForm(f => ({ ...f, is_active: e.target.checked }))} />
                  <span>Active</span>
                </label>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setLibraryModalOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleSavePosition} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
